import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import DLOLayout from './components/DLOLayout';
import DLOErrorProvider from './components/DLOErrorProvider';
import DLODashboard from './pages/DLODashboard';
import DLOLocationSetting from './pages/DLOLocationSetting';
import DLOCreateSchedule from './pages/DLOCreateSchedule/DLOCreateSchedule';
import DLOScheduleSetting from './pages/DLOScheduleSetting';
import { getSessionLockService } from '../../api/sessionLockService';
import { logger } from '../../utils/logger';

interface DLOAppProps {
  activeScreen: string;
  screenParams?: { [key: string]: any };
  onNavigate: (screen: string, params?: { [key: string]: any }) => void;
}

export default function DLOApp({ activeScreen, screenParams, onNavigate }: DLOAppProps) {
  // Create a callback for when lock expires - returns to DLO Dashboard
  const handleLockTimeout = () => {
    logger.log('[DLOApp] Lock timeout expired, navigating to DLO Dashboard');
    onNavigate('DLODashboard');
  };

  // Get selected location to detect changes
  const selectedLocation = useSelector((state: any) => state.appData.selectedLocation);

  // Redirect to DLODashboard when location changes while on a DLO sub-page
  const prevLocationIdRef = useRef<string | undefined>(selectedLocation?.id);
  useEffect(() => {
    const prevId = prevLocationIdRef.current;
    prevLocationIdRef.current = selectedLocation?.id;
    const isOnSubPage =
      activeScreen === 'DLOLocationSetting' ||
      activeScreen === 'DLOScheduleSetting' ||
      activeScreen === 'DLOCreateSchedule';
    if (prevId && selectedLocation?.id && prevId !== selectedLocation.id && isOnSubPage) {
      // Release all session locks before redirecting to the dashboard
      const lockService = getSessionLockService();
      if (lockService) {
        lockService.releaseAllLocks().catch((err) => {
          console.error('[DLO] Error releasing locks on location change:', err);
        });
      }
      onNavigate('DLODashboard');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id, activeScreen]);

  // Register timeout expiration callback for lock release
  useEffect(() => {
    const lockService = getSessionLockService();
    if (lockService) {
      lockService.setTimeoutExpirationCallback((resourceId: string) => {
        logger.log('[DLOApp] Lock timeout expired for resource:', resourceId);
        // The modal will handle navigation via its onNavigate prop
        // This callback is mainly for logging/cleanup purposes now
      });
    }
  }, []);

  const renderScreen = () => {
    switch (activeScreen) {
      case 'DLODashboard':
        return <DLODashboard onNavigate={onNavigate} />;
      case 'DLOLocationSetting':
        return <DLOLocationSetting onNavigate={onNavigate} />;
      case 'DLOCreateSchedule':
        return <DLOCreateSchedule onNavigate={onNavigate} />;
      case 'DLOScheduleSetting':
        return <DLOScheduleSetting onNavigate={onNavigate} activeTab={screenParams?.activeTab} scheduleType={screenParams?.scheduleType} />;
      default:
        return <DLODashboard onNavigate={onNavigate} />;
    }
  };

  return (
    <DLOErrorProvider onNavigate={handleLockTimeout}>
      <DLOLayout activeScreen={activeScreen} onNavigate={onNavigate}>
        {renderScreen()}
      </DLOLayout>
    </DLOErrorProvider>
  );
}
